<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-[90] bg-[hsl(var(--background))] flex flex-col">
      <!-- Header -->
      <div class="shrink-0 flex items-center gap-2 bg-sheet border-b border-[hsl(var(--border)/0.6)] px-3 py-3">
        <button
          @click="$emit('close')"
          class="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[hsl(var(--muted))] transition-colors shrink-0"
          :aria-label="$t('aboutProject.backButton')"
        >
          <ArrowLeft class="w-4.5 h-4.5" />
        </button>
        <h1 class="text-[15px] font-semibold">{{ $t('aboutProject.navTitle') }}</h1>
      </div>

      <div class="flex-1 overflow-y-auto">
        <div class="max-w-2xl mx-auto px-5 py-10 md:py-14">

          <!-- Hero -->
          <div class="flex flex-col items-center text-center gap-3 mb-10">
            <img :src="logoMark" alt="EcoWay" class="w-16 h-16 rounded-md shadow-ink-sm ring-1 ring-[hsl(var(--primary)/0.1)] object-cover" />
            <h2 class="text-[22px] font-semibold">EcoWay</h2>
            <p class="text-[14px] text-ink2 max-w-sm">{{ $t('aboutProject.hero.tagline') }}</p>
          </div>

          <!-- Content sections -->
          <div class="space-y-9">
            <section v-for="s in sections" :key="s.title">
              <h3 class="text-[16px] font-semibold mb-2.5">{{ s.title }}</h3>
              <p v-for="(p, i) in s.body" :key="i" class="text-[13.5px] leading-relaxed text-[hsl(var(--foreground)/0.85)]" :class="i > 0 ? 'mt-2.5' : ''">
                {{ p }}
              </p>
              <button
                v-if="s.cta"
                type="button"
                @click="s.cta.action"
                class="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[hsl(var(--primary))] hover:opacity-80 transition-opacity"
              >
                <component :is="s.cta.icon" class="w-4 h-4" />
                {{ s.cta.label }}
                <ArrowRight class="w-3.5 h-3.5" />
              </button>
            </section>
          </div>

          <!-- Closing brand line -->
          <p class="mt-9 text-center text-[13.5px] font-semibold text-[hsl(var(--primary))]">
            {{ $t('aboutProject.section4.closing') }}
          </p>

          <!-- Sponsors -->
          <div class="mt-12 pt-9 border-t border-[hsl(var(--border))]">
            <p class="text-[11px] text-ink2 font-semibold text-center mb-5">
              {{ $t('aboutProject.sponsors.title') }}
            </p>
            <div class="flex flex-wrap items-start justify-center gap-4">
              <div class="flex flex-col items-center gap-1.5 w-[110px]">
                <div class="h-16 w-full px-5 rounded-md bg-card border border-border flex items-center justify-center shadow-ink-sm">
                  <img :src="kustoLogo" alt="Kusto Group" class="max-h-9 max-w-[130px] object-contain" />
                </div>
                <span class="text-[11px] text-ink2 text-center leading-tight">Kusto Group</span>
              </div>
              <div class="flex flex-col items-center gap-1.5 w-[110px]">
                <div class="h-16 w-full px-5 rounded-md bg-card border border-border flex items-center justify-center shadow-ink-sm">
                  <img :src="visitAlmatyLogo" alt="Visit Almaty" class="max-h-9 max-w-[130px] object-contain" />
                </div>
                <span class="text-[11px] text-ink2 text-center leading-tight">Visit Almaty</span>
              </div>
              <div class="flex flex-col items-center gap-1.5 w-[110px]">
                <div class="h-16 w-full px-5 rounded-md bg-card border border-border flex items-center justify-center shadow-ink-sm">
                  <img :src="mesLogo" :alt="$t('aboutProject.sponsors.mes')" class="max-h-10 max-w-[60px] object-contain" />
                </div>
                <span class="text-[11px] text-ink2 text-center leading-tight">{{ $t('aboutProject.sponsors.mes') }}</span>
              </div>
              <div class="flex flex-col items-center gap-1.5 w-[110px]">
                <div class="h-16 w-full px-5 rounded-md bg-card border border-border flex items-center justify-center shadow-ink-sm">
                  <img :src="parkLogo" :alt="$t('aboutProject.sponsors.park')" class="max-h-11 max-w-[60px] object-contain" />
                </div>
                <span class="text-[11px] text-ink2 text-center leading-tight">{{ $t('aboutProject.sponsors.park') }}</span>
              </div>
            </div>
          </div>

          <!-- CTA -->
          <div class="mt-12 rounded-md bg-[hsl(var(--primary)/0.06)] border border-[hsl(var(--primary)/0.15)] p-5 text-center">
            <p class="text-[15px] font-semibold mb-1.5">{{ $t('aboutProject.cta.title') }}</p>
            <p class="text-[13px] text-ink2 leading-relaxed mb-4">{{ $t('aboutProject.cta.body') }}</p>
            <button
              type="button"
              @click="feedback.open('about-project')"
              class="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-[hsl(var(--primary))] text-white text-[13px] font-semibold hover:bg-[hsl(158,38%,30%)] transition-colors shadow-ink-sm"
            >
              <Send class="w-3.5 h-3.5" />
              {{ $t('aboutProject.cta.button') }}
            </button>
          </div>

          <!-- Footer -->
          <div class="mt-12 pt-6 border-t border-[hsl(var(--border))]">
            <p class="text-center text-[12.5px] font-semibold flex items-center justify-center gap-1.5">
              EcoWay <Heart class="w-3.5 h-3.5 fill-[hsl(var(--danger))] text-[hsl(var(--danger))]" /> {{ $t('aboutProject.brandLine') }}
            </p>

            <p class="text-center text-[10.5px] text-ink2 mt-3">
              {{ $t('app.footer.copyright', { year: currentYear }) }}
            </p>

            <div class="mt-5 flex flex-col items-center gap-1">
              <p class="text-[10px] text-ink2 font-semibold mb-1">
                {{ $t('aboutProject.contactsTitle') }}
              </p>
              <a href="mailto:hello@ecoway.kz" class="text-[12.5px] font-semibold text-[hsl(var(--primary))] underline underline-offset-2">hello@ecoway.kz</a>
              <a href="tel:+77718250443" class="text-[12.5px] font-semibold text-[hsl(var(--primary))] underline underline-offset-2">+7 771 825 04 43</a>
            </div>

            <div class="mt-7">
              <p class="text-[10px] text-ink2 font-semibold text-center mb-2.5">
                {{ $t('aboutProject.documents.title') }}
              </p>
              <div class="flex flex-col items-center gap-1.5">
                <button
                  v-for="doc in documents"
                  :key="doc.label"
                  @click="openDocument(doc.action)"
                  class="text-[12px] text-[hsl(var(--foreground)/0.75)] hover:text-[hsl(var(--primary))] hover:underline transition-colors"
                >{{ doc.label }}</button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Ready documents: shown as modals (same content as the registration
         form's own consent checkboxes), not full pages, since reading them
         here is a quick reference rather than something that should
         navigate the user away. -->
    <LegalDocumentModal v-if="showServiceRules" :document="serviceRules" @close="showServiceRules = false" />
    <LegalDocumentModal v-if="showDchsDataConsent" :document="dchsDataConsent" @close="showDchsDataConsent = false" />
    <LegalDocumentModal v-if="showVideoGreetingConsent" :document="videoGreetingConsent" @close="showVideoGreetingConsent = false" />

    <!-- Ready document: full privacy policy page -->
    <PrivacyPolicyView v-if="showPrivacyPolicy" @close="showPrivacyPolicy = false" />

    <!-- Ready document: full terms-of-service page -->
    <TermsOfServiceView v-if="showTermsOfService" @close="showTermsOfService = false" />

    <!-- Ready document: full data-consent page -->
    <DataConsentView v-if="showDataConsent" @close="showDataConsent = false" />

  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { ArrowLeft, ArrowRight, Send, Heart, Radio, Shield } from "lucide-vue-next";
import logoMark from "../../assets/logo-mark.png";
import kustoLogo from "../../assets/sponsors/kusto-logo.png";
import visitAlmatyLogo from "../../assets/sponsors/visit-almaty-logo.png";
import mesLogo from "../../assets/sponsors/mes-logo.png";
import parkLogo from "../../assets/sponsors/park-logo.svg";
import {
  serviceRules,
  dchsDataConsent,
  videoGreetingConsent,
} from "../../content/legalDocuments";
import LegalDocumentModal from "../LegalDocumentModal.vue";
import PrivacyPolicyView from "./PrivacyPolicyView.vue";
import TermsOfServiceView from "./TermsOfServiceView.vue";
import DataConsentView from "./DataConsentView.vue";
import { useHistoryBackClose } from "../../composables/useHistoryBackClose";
import { useFeedbackStore } from "../../store/feedback";

const emit = defineEmits<{
  (e: "close"): void;
}>();
useHistoryBackClose(() => emit("close"));

const { t } = useI18n();
const feedback = useFeedbackStore();
const router = useRouter();

const currentYear = new Date().getFullYear();

// CTA-ссылка после последнего абзаца секции (не после каждого абзаца) - по
// скрину от ПМа: иконка + текст + стрелка, без рамки. "Добавить наблюдение"
// переиспользует тот же feedback.open('about-project'), что и большая CTA-
// карточка ниже сайдбаров (см. шаблон) - оставляем About-оверлей открытым,
// диалог фидбека стекуется поверх (z-[100] > z-[90]), как и та карточка.
// "Смотреть Live"/"Безопасность" - настоящая навигация, здесь оверлей нужно
// закрыть, иначе пункт назначения окажется скрыт под ним.
const openObservation = () => feedback.open("about-project");
const goToLive = () => {
  emit("close");
  router.push("/live");
};
const goToSafety = () => {
  emit("close");
  router.push("/profile/safety");
};

const sections = computed(() => [
  {
    title: t("aboutProject.section1.title"),
    body: [t("aboutProject.section1.body1"), t("aboutProject.section1.body2")],
    cta: { icon: Send, label: t("aboutProject.section1.cta"), action: openObservation },
  },
  {
    title: t("aboutProject.section2.title"),
    body: [t("aboutProject.section2.body1"), t("aboutProject.section2.body2")],
    cta: { icon: Radio, label: t("aboutProject.section2.cta"), action: goToLive },
  },
  {
    title: t("aboutProject.section3.title"),
    body: [t("aboutProject.section3.body1"), t("aboutProject.section3.body2")],
    cta: { icon: Shield, label: t("aboutProject.section3.cta"), action: goToSafety },
  },
  { title: t("aboutProject.section4.title"), body: [t("aboutProject.section4.body1"), t("aboutProject.section4.body2"), t("aboutProject.section4.body3")] },
]);

// "serviceRules"/"dchsDataConsent" открываются модалками (как и в двух чекбоксах
// согласия в RouteRegistrationDialog.vue), а не отдельными страницами, чтобы
// чтение документа не уводило пользователя со страницы регистрации.
const documents = computed(() => [
  { label: t("aboutProject.documents.privacyPolicy"), action: "privacyPolicy" as const },
  { label: t("aboutProject.documents.termsOfService"), action: "termsOfService" as const },
  { label: t("aboutProject.documents.dataConsent"), action: "dataConsent" as const },
  { label: t("aboutProject.documents.serviceRules"), action: "serviceRules" as const },
  { label: t("aboutProject.documents.routeDataConsent"), action: "dchsDataConsent" as const },
  { label: t("aboutProject.documents.videoGreetingConsent"), action: "videoGreetingConsent" as const },
]);

const showServiceRules = ref(false);
const showDchsDataConsent = ref(false);
const showPrivacyPolicy = ref(false);
const showTermsOfService = ref(false);
const showDataConsent = ref(false);
const showVideoGreetingConsent = ref(false);

const openDocument = (action: "privacyPolicy" | "termsOfService" | "dataConsent" | "serviceRules" | "dchsDataConsent" | "videoGreetingConsent") => {
  if (action === "privacyPolicy") showPrivacyPolicy.value = true;
  else if (action === "termsOfService") showTermsOfService.value = true;
  else if (action === "dataConsent") showDataConsent.value = true;
  else if (action === "serviceRules") showServiceRules.value = true;
  else if (action === "dchsDataConsent") showDchsDataConsent.value = true;
  else showVideoGreetingConsent.value = true;
};
</script>
